import { Dimensions } from '@remotion/layout-utils';
import { DropPosition } from '../../assets/add-asset';
import { WebpAsset } from '../../assets/assets';
import { cacheAssetLocally } from '../../caching/indexeddb';
import { byDefaultKeepAspectRatioMap } from '../../utils/aspect-ratio';
import { calculateMediaDimensionsForCanvas } from '../../utils/dimension-utils';
import { generateRandomId } from '../../utils/generate-random-id';
import { WebpItem } from './webp-item-type';
import { getWebpDurationInSeconds } from './get-webp-duration';

export const makeWebpItem = async ({
  file,
  currentFrame,
  dropPosition,
  dimensions,
  assetId,
  fps,
  compositionWidth,
  compositionHeight,
  filename,
  remoteUrl,
  remoteFileKey,
  durationInSeconds,
}: {
  file: Blob;
  currentFrame: number;
  dropPosition: DropPosition | null;
  dimensions: Dimensions;
  assetId: string;
  fps: number;
  compositionWidth: number;
  compositionHeight: number;
  remoteUrl: string | null;
  remoteFileKey: string | null;
  filename: string;
  /** 아이템 duration 고정값 (선택, 없으면 WebP 실제 재생 시간) */
  durationInSeconds?: number;
}): Promise<{ item: WebpItem; asset: WebpAsset }> => {
  const id = generateRandomId();
  await cacheAssetLocally({
    assetId: id,
    value: file,
  });

  const webpDuration = await getWebpDurationInSeconds(file);
  const actualDuration = durationInSeconds ?? webpDuration;
  const durationInFrames = Math.floor(actualDuration * fps);

  const content = calculateMediaDimensionsForCanvas({
    mediaWidth: dimensions.width,
    mediaHeight: dimensions.height,
    containerWidth: compositionWidth,
    containerHeight: compositionHeight,
    dropPosition,
  });

  const asset: WebpAsset = {
    id: assetId,
    type: 'webp',
    durationInSeconds: webpDuration,
    filename,
    remoteUrl,
    remoteFileKey,
    size: file.size,
    mimeType: file.type,
    width: dimensions.width,
    height: dimensions.height,
  };

  const item: WebpItem = {
    id,
    durationInFrames,
    webpStartFromInSeconds: 0,
    top: content.top,
    left: content.left,
    width: content.width,
    height: content.height,
    from: currentFrame,
    type: 'webp',
    opacity: 1,
    borderRadius: 0,
    rotation: 0,
    playbackRate: 1,
    assetId: asset.id,
    isDraggingInTimeline: false,
    keepAspectRatio: byDefaultKeepAspectRatioMap.webp,
    fadeInDurationInSeconds: 0,
    fadeOutDurationInSeconds: 0,
    cropLeft: 0,
    cropTop: 0,
    cropRight: 0,
    cropBottom: 0,
  };

  return { item, asset };
};
